import { useState, type ReactNode } from 'react';

export function Panel({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-rule bg-slate p-4">
      <header className="mb-3 flex items-baseline justify-between gap-4">
        <h2 className="text-xs tracking-[0.14em] text-ink-dim uppercase">{title}</h2>
        {aside ? <div className="text-[11px] text-ink-dim">{aside}</div> : null}
      </header>
      {children}
    </section>
  );
}

/** §10: обязательные состояния проектируются явно. */
export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="rounded border border-dashed border-rule px-4 py-10 text-center">
      <p className="text-ink">{title}</p>
      {hint ? <p className="mt-1 text-sm text-ink-dim">{hint}</p> : null}
    </div>
  );
}

export function Pagination({
  total,
  page,
  pageSize,
  onPage,
}: {
  total: number;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <nav className="mt-3 flex items-center justify-between gap-4 text-sm text-ink-dim">
      <span>{`${from}–${to} из ${total}`}</span>
      <span className="flex gap-2">
        <button
          type="button"
          aria-label="Предыдущая страница"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="min-h-11 min-w-11 rounded border border-rule px-2 py-1 disabled:opacity-40"
        >
          ◂
        </button>
        <button
          type="button"
          aria-label="Следующая страница"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          className="min-h-11 min-w-11 rounded border border-rule px-2 py-1 disabled:opacity-40"
        >
          ▸
        </button>
      </span>
    </nav>
  );
}

/** §4.3: моноширинный только для техметок. */
export function TechKey({ value }: { value: string }) {
  return <span className="font-mono text-[11px] text-ink-dim">{value}</span>;
}

/**
 * §5.5: отсутствие фото не делает предложение второсортным.
 *
 * Два РАЗНЫХ факта, которые нельзя смешивать:
 *  · has_photo === false — фотографии нет у источника (у Travelata её нет
 *    в принципе). Это свойство данных, а не сбой;
 *  · onError — ссылка есть, но CDN её не отдал. static.tourvisor.ru отвечает
 *    404 на живые с виду ссылки; без обработчика остаётся сломанная иконка
 *    и «съехавшая» высота карточки (Д-Ф17).
 *
 * Оба случая дают заполнитель ТОЙ ЖЕ высоты — вёрстка не должна зависеть
 * от успеха сетевого запроса. Подписи разные, потому что факты разные.
 */
export function Photo({
  url,
  hasPhoto,
  alt,
  height,
  priority = false,
}: {
  url: string | null;
  hasPhoto: boolean;
  alt: string;
  height: number;
  /** Первый смысловой кадр страницы загружается сразу; остальные экономят сеть. */
  priority?: boolean;
}) {
  // Ключ сброса — сам url: новая ссылка получает новую попытку загрузки,
  // иначе один сбой навсегда «выключал» бы фото в переиспользуемой строке таблицы.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const isBroken = url !== null && failedUrl === url;
  const label = isBroken ? 'фото не загрузилось' : 'фото нет';

  if (!hasPhoto || url === null || isBroken) {
    return (
      <div
        data-testid="photo-placeholder"
        {...(isBroken ? { 'data-photo-failed': 'true' } : {})}
        style={{ height: `${height}px` }}
        className="flex w-full items-center justify-center rounded border border-rule bg-abyss"
      >
        {/*
          🔴 Найдено живым осмотром каталога 2026-07-30: в строке таблицы
          заглушка 48×32 px, и подпись «фото не загрузилось» вылезала за неё
          обрезками «рто не / згрузилось». В маленьком размере смысл несёт не
          текст, а само место: подпись сжимается до значка, а полная фраза
          остаётся в `title` для наведения и для чтения с экрана.
        */}
        {height < 56 ? (
          <span aria-label={label} title={label} className="text-[11px] text-ink-dim">
            {isBroken ? '⃠' : '—'}
          </span>
        ) : (
          <span className="text-[11px] text-ink-dim">{label}</span>
        )}
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      onError={() => setFailedUrl(url)}
      style={{ height: `${height}px` }}
      className="w-full rounded object-cover"
    />
  );
}
